import { BetrokkenTrein, useBusEvacuatieAfronden } from "@/entities/betrokken-treinen";
import { StationsAutocomplete } from "@/features/infra";
import {
    Button,
    Dialog,
    DialogBody,
    DialogContainer,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    ErrorDisplay,
    Form,
    FormFieldInputTime,
} from "@/shared/components";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dispatch, SetStateAction } from "react";
import { useForm } from "react-hook-form";
import z from "zod";

const busEvacuatieAfrondenSchema = z.object({
    naarStation: z.string().nonempty({ message: "Vul het afzetstation in" }),
    etaNaarStation: z.string().nonempty({ message: "Vul de ETA in" }),
});

type BusEvacuatieAfrondenData = z.infer<typeof busEvacuatieAfrondenSchema>;

type BusEvacuatieAfrondenProps = {
    dossierId: string;
    open: boolean;
    onOpenChange: Dispatch<SetStateAction<boolean>>;
    trein: BetrokkenTrein;
    defaultValues?: Partial<BusEvacuatieAfrondenData>;
};

export function BusEvacuatieAfronden({ dossierId, trein, open, onOpenChange, defaultValues }: BusEvacuatieAfrondenProps) {
    const form = useForm<BusEvacuatieAfrondenData>({
        resolver: zodResolver(busEvacuatieAfrondenSchema),
        defaultValues: {
            naarStation: defaultValues?.naarStation ?? "",
            etaNaarStation: defaultValues?.etaNaarStation ?? "",
        },
    });
    const { mutate, error, isPending } = useBusEvacuatieAfronden();

    // Destructure formState here to ensure updates, see https://github.com/react-hook-form/react-hook-form/issues/1146
    const {
        formState: { dirtyFields, isDirty },
    } = form;

    function onSubmit(data: BusEvacuatieAfrondenData) {
        const dirtyKeys = Object.keys(dirtyFields) as Array<keyof BusEvacuatieAfrondenData>;
        const currentStatus = trein.busEvacuatie!.status;

        const payload = dirtyKeys.reduce((acc: Partial<BusEvacuatieAfrondenData>, key) => {
            acc[key] = data[key];
            return acc;
        }, {});

        mutate(
            { dossierId, treinId: trein.id, currentStatus, ...payload },
            {
                onSuccess: () => {
                    onOpenChange(false);
                },
            }
        );
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContainer className="sm:max-w-[519px]">
                <Form {...form}>
                    <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}>
                        <DialogHeader>
                            <DialogTitle>Afronden evacuatiebus {BetrokkenTrein.formatTrainLabel(trein)}</DialogTitle>
                        </DialogHeader>
                        <DialogBody className="grid gap-2">
                            <FormFieldInputTime
                                form={form}
                                name="etaNaarStation"
                                label="ETA afzetstation"
                                helptext="Geef aan hoe laat de evacuatiebussen op het afzetstation zullen aankomen"
                            />
                            <StationsAutocomplete form={form} name="naarStation" label="Afzetstation" />
                            {error && <ErrorDisplay error={error} />}
                        </DialogBody>
                        <DialogFooter>
                            <Button type="button" variant="tertiary" onClick={() => onOpenChange(false)}>
                                Annuleren
                            </Button>
                            <Button type="submit" variant="primary" disabled={isPending || !isDirty}>
                                Evacuatie afronden
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContainer>
        </Dialog>
    );
}
